/**
 * Google Analytics (GA4) + Google Tag Manager bootstrap.
 *
 * Initialized from gatsby-browser.js (onClientEntry) instead of from inside a
 * React component / Helmet. This guarantees gtag is set up on EVERY full page
 * load - including pages that do not render <Layout> (e.g. handlePaymentResponse)
 * - and removes the hydration/Helmet timing races we hit previously.
 *
 * gtag.js is the primary GA4 channel. GTM is loaded AFTER gtag.js (optional, for
 * other tags), so gtag.js deterministically owns the GA4 property and reliably
 * sends hits regardless of cache state.
 *
 * sendPageView is called from onRouteUpdate, which fires on the initial load and
 * on every client-side route change.
 */

// GA4 Measurement IDs per production host.
// UAT hosts and localhost are intentionally absent, so GA/GTM do not load there.
const GA4_IDS = {
  'be.scplus.co': 'G-M7L20HRRWZ',
  'fi.scplus.co': 'G-T2VEEDRQGZ',
  'nl.scplus.co': 'G-FT8DTR06T0',
  'it.scplus.co': 'G-VS8WM7VM02',
  'de.scplus.co': 'G-GRFFFL6TEG',
  'pl.scplus.co': 'G-C9ZNQK6569',
  'cz.scplus.co': 'G-KCS7SHB99J',
  'at.scplus.co': 'G-3FNEMSST13',
}

// GTM container IDs per production host.
const GTM_IDS = {
  'be.scplus.co': 'GTM-KK3CHVP',
  'fi.scplus.co': 'GTM-MLWB2BJ',
  'nl.scplus.co': 'GTM-W5FCJPZ',
  'it.scplus.co': 'GTM-KF29GQ6J',
  'de.scplus.co': 'GTM-TQP8CFXK',
  'pl.scplus.co': 'GTM-MCVQLTG6',
  'cz.scplus.co': 'GTM-K3N8L23V',
  'at.scplus.co': 'GTM-MWJG8T44',
}

// Maps a (locale-stripped) page path to a custom GA4 event name.
const PAGE_EVENT_MAP = {
  '/': 'pageview_landing',
  '/device-info': 'pageview_device_info',
  '/device-eligibility': 'pageview_device_eligibility',
  '/select-product': 'pageview_product',
  '/personal-info': 'pageview_personal_info',
  '/order-summary': 'pageview_order_summary',
  '/payment-success': 'pageview_payment_success',
  '/payment-failed': 'pageview_payment_failed',
}

export const getPageEventName = () => {
  const path = window.location.pathname
  // Strip locale prefix (e.g. /de_de/device-info -> /device-info) and trailing slash.
  const cleanPath = path.replace(/^\/[a-z]{2}_[a-z]{2}/, '').replace(/\/$/, '') || '/'
  return PAGE_EVENT_MAP[cleanPath] || 'pageview_other'
}

const loadGTM = () => {
  const gtmId = GTM_IDS[window.location.host]
  if (!gtmId) return
  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' })
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://www.googletagmanager.com/gtm.js?id=' + gtmId
  document.head.appendChild(script)
}

let initialized = false

/**
 * Sets up gtag.js, then loads GTM after gtag.js is ready.
 * Safe to call multiple times; only the first call has an effect.
 */
export const initGA = () => {
  if (typeof window === 'undefined' || initialized) return
  const ga4Id = GA4_IDS[window.location.host]

  window.dataLayer = window.dataLayer || []
  window.dataLayer.push({ platform: 'gatsby' })
  // Define gtag synchronously so events queue safely before gtag.js loads.
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments) }

  if (!ga4Id) {
    // Not a production host -> no GA4, but still allow GTM if configured.
    initialized = true
    loadGTM()
    return
  }

  initialized = true
  window.gtag('js', new Date())
  // send_page_view: false -> we send our own custom page-view events instead.
  window.gtag('config', ga4Id, { send_page_view: false })

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://www.googletagmanager.com/gtag/js?id=' + ga4Id
  // Load GTM only after gtag.js is ready so gtag.js owns GA4 first (no race).
  script.onload = loadGTM
  script.onerror = loadGTM
  document.head.appendChild(script)
}

/**
 * Sends a custom page-view event to GA4 via gtag.
 */
export const sendPageView = () => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return
  window.gtag('event', getPageEventName(), {
    page_title: document.title,
    page_location: window.location.href,
    page_path: window.location.pathname,
  })
}
